import React, { useState, useEffect } from "react";
import "../style/Self.css";
import pencil from "../assets/pencil.png"
import StarGen from "../Component/StarSet.jsx"
import { Link } from "react-router-dom";
import QuizNoRoute from "../Component/QuizNoRoute";
import dishesData from '../assets/dishes2.json';
import Null_Photo from "../assets/No_Photo.png";
import defaultAvatar from "../assets/OIP.jpg";

export const Self = () => {
  // Auth & Login State
  const userToken = sessionStorage.getItem("token");
  const userId = sessionStorage.getItem("user");
  const isLoggedIn = Boolean(userToken && userId);
  const isLocalhost = window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1";
  const BASE_IMAGE_URL = isLocalhost ? "" : "https://webdev.cse.buffalo.edu/hci/teams/indecisive";
  const resolveImagePath = (imagePath) => {
    if (!imagePath) return Null_Photo;
    if (imagePath.startsWith("http")) return imagePath;
    return `${BASE_IMAGE_URL}${imagePath}`;
  };
    
  // UI State
  const [modalOpen, setModalOpen] = useState(false);
  const [EditmodalOpen, setEditModalOpen] = useState(false);
  const [selectedTab, setSelectedTab] = useState("reviews");
  const [isEditing, setIsEditing] = useState(false);

  // User Profile Data State
  const [username, setUsername] = useState("Username");
  const [picture, setPicture] = useState(defaultAvatar);
  const [description, setDescription] = useState("Your description here.");
  const [newDescription, setNewDescription] = useState(description);
  const [followerCount, setFollowerCount] = useState(0);
  const [followingCount, setFollowingCount] = useState(0);
  const [posts, setPosts] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [filter, setPreferences] = useState([]); // For user preferences (Food Topics)
  
  // Dish ratings data
  const [dishRatings, setDishRatings] = useState({});

  // Additional state for user attributes (for merging updates)
  const [userAttributes, setUserAttributes] = useState({});

  // Loading and Error States
  const [isUserLoading, setIsUserLoading] = useState(true);
  const [isPostsLoading, setIsPostsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Sync newDescription when description updates (for editing)
  useEffect(() => {
    setNewDescription(description);
  }, [description]);

  // --- Fetch User Data with Follower/Following Logic ---
  useEffect(() => {
    if (!isLoggedIn || !userId) {
      console.warn("User not logged in or userId missing. Aborting user data fetch.");
      setIsUserLoading(false);
      setIsPostsLoading(false);
      return;
    }

    setIsUserLoading(true);
    setError(null);

    const fetchUserData = async () => {
      try {
        // Fetch user details
        const response = await fetch(
          `${process.env.REACT_APP_API_PATH}/users/${userId}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${userToken}`,
            },
          }
        );

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          console.error("Failed to fetch user data. Status:", response.status, "Response:", errorData);
          throw new Error(errorData.message || `Failed to fetch user data (${response.status})`);
        }

        const result = await response.json();
        // console.log("User data fetched:", result);

        if (result && result.attributes) {
          // Save full attributes for future updates
          setUserAttributes(result.attributes);
          setUsername(result.attributes.username || `User${userId}`);
          let profilePic = result.attributes.picture || defaultAvatar;
          setPicture(profilePic);
          setDescription(result.attributes.description || "Your description here.");
          setFavorites(result.attributes.favorites || []);
          setPreferences(result.attributes.filter || []);
        } else {
          console.error("User data structure might be missing attributes:", result);
          setUsername(`User${userId}`);
          setPicture(defaultAvatar);
          setDescription("Your description here.");
          setFavorites([]);
          setPreferences([]);
        }

        // --- Follower/Following Logic ---
        const getAccountConnections = async () => {
          const connResponse = await fetch(
            `${process.env.REACT_APP_API_PATH}/connections?anyUserID=${userId}`
          );
          return connResponse.json();
        };

        const connectionHandler = async () => {
          const jsonInfo = await getAccountConnections();
          let followers = 0;
          let following = 0;
          
          // Assume jsonInfo[0] is an array of connection objects
          if (Array.isArray(jsonInfo[0])) {
            for (let conn = 0; conn < jsonInfo[0].length; conn++) {
              let curr = jsonInfo[0][conn];
              
              // Count followers: connections where current user is the "toUserID"
              if (curr.toUserID == userId) {
                followers++;
              } 
              // Count following: connections where current user is the "fromUserID"
              else if (curr.fromUserID == userId) {
                following++;
              }
            }
          }
          
          // console.log(`Connection stats: ${followers} followers, ${following} following`);
          setFollowerCount(followers);
          setFollowingCount(following);
        };

        await connectionHandler();
      } catch (error) {
        console.error("Error fetching user data:", error);
        setError(error.message || "Error loading profile data.");
      } finally {
        setIsUserLoading(false);
      }
    };

    fetchUserData();
  }, [isLoggedIn, userToken, userId]);

  // --- Fetch User's Posts ---
  useEffect(() => {
    if (!isLoggedIn || !userId) {
      setIsPostsLoading(false);
      return;
    }

    setIsPostsLoading(true);

    const fetchUserPosts = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_PATH}/posts?authorID=${userId}&parentID=`,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${userToken}`,
            },
          }
        );
        if (!response.ok) {
          throw new Error("Failed to fetch posts");
        }

        const result = await response.json();
        // console.log("Posts data fetched:", result);
        let postsData = [];
        if (Array.isArray(result) && result.length === 2 && Array.isArray(result[0])) {
          postsData = result[0];
        } else if (Array.isArray(result)) {
          postsData = result;
        } else {
          console.warn("Unexpected posts data structure:", result);
        }
        
        setPosts(postsData);
      } catch (error) {
        console.error("Error fetching posts:", error);
        setError(error.message || "Error loading posts.");
      } finally {
        setIsPostsLoading(false);
      }
    };

    fetchUserPosts();
  }, [isLoggedIn, userId, userToken]);

  // --- Fetch dish ratings for favorites ---
  useEffect(() => {
    if (!favorites || favorites.length === 0 || !isLoggedIn) return;
    
    const fetchDishRatings = async () => {
      try {
        // Get all posts to find reviews for each favorite dish
        const response = await fetch(
          `${process.env.REACT_APP_API_PATH}/posts?parentID=&limit=100`,
          {
            headers: {
              "Content-Type": "application/json",
              ...(userToken && { "Authorization": `Bearer ${userToken}` })
            },
          }
        );
        
        if (!response.ok) {
          throw new Error("Failed to fetch posts for ratings");
        }
        
        const result = await response.json();
        if (!result || !result[0] || !Array.isArray(result[0])) {
          console.warn("Unexpected posts data structure for ratings:", result);
          return;
        }
        
        const allPosts = result[0];
        const ratings = {};
        
        // For each favorite, find reviews and calculate average rating
        favorites.forEach(favorite => {
          const dishId = favorite.id;
          if (!dishId) return;
          
          // Find all reviews for this dish
          const dishReviews = allPosts.filter(post => 
            post.attributes?.dishID && 
            parseInt(post.attributes.dishID, 10) === parseInt(dishId, 10)
          );
          
          // If dish has reviews, calculate average rating
          if (dishReviews && dishReviews.length > 0) {
            const totalRating = dishReviews.reduce((sum, review) => {
              const rating = parseFloat(review.attributes.overallRating || review.attributes.rating) || 0;
              return sum + rating;
            }, 0);
            
            const avgRating = totalRating / dishReviews.length;
            ratings[dishId] = {
              averageRating: Math.round(avgRating * 10) / 10,
              reviewCount: dishReviews.length
            };
          } else {
            // No reviews found for this dish
            ratings[dishId] = { averageRating: 0, reviewCount: 0 };
            
            // Check if we can find this dish in dishes2.json for any preset ratings
            const dishData = dishesData.find(dish => dish.id === parseInt(dishId, 10));
            if (dishData && dishData.reviews && dishData.reviews.length > 0) {
              const staticReviews = dishData.reviews;
              const staticTotalRating = staticReviews.reduce((sum, review) => {
                return sum + (parseFloat(review.overallRating) || 5);
              }, 0);
              
              ratings[dishId] = {
                averageRating: Math.round((staticTotalRating / staticReviews.length) * 10) / 10,
                reviewCount: staticReviews.length
              };
            }
          }
        });
        
        setDishRatings(ratings);
      } catch (error) {
        console.error("Error fetching dish ratings:", error);
      }
    };
    
    fetchDishRatings();
  }, [favorites, isLoggedIn, userToken]);

  // --- Description Editing Logic ---
  const handleDescriptionSave = async () => {
    if (!userAttributes || Object.keys(userAttributes).length === 0) {
      console.error("User attributes not loaded yet. Cannot save description.");
      
      setIsEditing(false);
      return;
    }
    const updatedAttributesPayload = { ...userAttributes, description: newDescription };

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_PATH}/users/${userId}`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${userToken}`,
          },
          body: JSON.stringify({ attributes: updatedAttributesPayload }),
        }
      );

      if (!response.ok) {
        const errorBody = await response.text();
        console.error(`Failed to update description. Status: ${response.status}, Body: ${errorBody}`);
        throw new Error("Failed to update description");
      }

      const result = await response.json();
      // console.log("Description updated successfully:", result);
      setUserAttributes(updatedAttributesPayload);
      setDescription(newDescription);
    } catch (error) {
      console.error("Error updating description:", error);
      
    } finally {
      setIsEditing(false);
    }
  };


  
  // --- Preferences Modal Component ---
  function Modal({ onClose }) {
    return (
      <div className="pref-pop">
        <div>
        <h3 className="modalcustom">Your Food Choices</h3>
          {filter && filter.length > 0 ? (
    filter.map((item, index) => (
      <div key={index} className="option">
        {item}
      </div>
    ))
  ) : (
    <p>No food choices set.</p>
  )}
<br></br> <br></br>
          <button className="close-pref" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    );
  }

  // --- Utility: Chunk Array into Rows ---
  const chunkArray = (array, chunkSize) => {
    const chunks = [];
    for (let i = 0; i < array.length; i += chunkSize) {
      chunks.push(array.slice(i, i + chunkSize));
    }
    return chunks;
  };

  // --- DishCard Component with Dynamic Navigation ---
  const DishCard = ({ postId, title, review, restaurant, overallRating, image, linkType }) => {
    let linkPath = "#";
    if (postId) {
      if (linkType === "review") {
        linkPath = `/reviewPost/${postId}`;
      } else if (linkType === "favorite") {
        linkPath = `/dish/${postId}`;
      }
    }
    const displayTitle = title || "Untitled Dish";
    const displayImage = resolveImagePath(image);
    const displayReview = review || "";
    const displayRestaurant = restaurant || "";
    
    // For favorites, use the calculated average rating from dishRatings
    const displayRating = linkType === "favorite" && dishRatings[postId] 
      ? dishRatings[postId].averageRating 
      : overallRating;
      
    // Only show rating if there are reviews (for favorites)
    const hasRating = linkType === "favorite" 
      ? (dishRatings[postId] && dishRatings[postId].reviewCount > 0)
      : !!overallRating;

    return (
      <Link to={linkPath} style={{ textDecoration: "none", color: "inherit" }}>
        <div className="dish">
          <img
            className="dish-img"
            alt={displayTitle}
            src={displayImage}
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = Null_Photo;
            }}
          />
          <div className="dish-info">
            <h2>{displayTitle}</h2>
            {hasRating && (
              <div className="rating">
                <StarGen rating={displayRating} />
                {linkType === "favorite" && dishRatings[postId] && dishRatings[postId].reviewCount > 0 && (
                  <span className="review-count">({dishRatings[postId].reviewCount} {dishRatings[postId].reviewCount === 1 ? 'review' : 'reviews'})</span>
                )}
              </div>
            )}
            {displayReview && <p>{displayReview}</p>}
            {displayRestaurant && <p><strong>{displayRestaurant}</strong></p>}
          </div>
        </div>
      </Link>
    );
  };

  // --- DishList Component for Reviews ---
  const DishList = () => {
    if (isPostsLoading) {
      return (
        <div className="loading-message">
          <p>Loading reviews...</p>
        </div>
      );
    }
    if (!posts || posts.length === 0) {
      return (
        <div className="no-content-message">
          <h2>No Reviews Yet</h2>
        </div>
      );
    }
    const postRows = chunkArray(posts, 2);
    return (
      <div className="dishes-container">
        {postRows.map((row, rowIndex) => (
          <div key={rowIndex} className="dishes-row">
            {row.map((post) => {
              if (!post?.id || !post?.attributes) return null;
              const attrs = post.attributes;
              return (
                <DishCard
                  key={post.id}
                  postId={post.id}
                  title={attrs.title || "Untitled Review"}
                  review={attrs.review || post.content}
                  restaurant={attrs.restaurant || attrs.associatedRestaurant || "Unknown"}
                  overallRating={attrs.rating || attrs.overallRating}
                  image={attrs.image}
                  linkType="review"
                />
              );
            })}
          </div>
        ))}
      </div>
    );
  };

  // --- FavoritesList Component for Favorites ---
  const FavoritesList = () => {
    if (!favorites || favorites.length === 0) {
      return (
        <div className="no-content-message">
          <h2>No Favorites Yet</h2>
          <p>Find dishes you love and click the heart to save them here!</p>
        </div>
      );
    }

    const favoritesRows = chunkArray(favorites, 2);
    return (
      <div className="dishes-container">
        {favoritesRows.map((row, rowIndex) => (
          <div key={rowIndex} className="dishes-row">
            {row.map((favorite, idx) => (
              <DishCard
                key={idx}
                postId={favorite.id || favorite.postId}
                title={favorite.title || "Favorite Dish"}
                review={favorite.review || ""}
                restaurant={favorite.restaurant || ""}
                overallRating={favorite.overallRating || 5}
                image={favorite.image || "/placeholder.png"}
                linkType="favorite"
              />
            ))}
          </div>
        ))}
      </div>
    );
  };

  // --- Main Render Logic ---
  if (!isLoggedIn) {
    return (
      <div className="self-container">
        <div className="overlay">
          <div className="login-prompt">
            <h2>Please Log In or Sign Up</h2>
            <p>Access your profile, reviews, and favorites.</p>
            <div className="button-group">
              <Link to="/login" className="button login-button">Login</Link>
              <Link to="/signup" className="button signup-button">Sign Up</Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (isUserLoading) {
    return (
      <div className="self-container loading-container">
        <p className="loading-indicator">Loading profile...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="self-container error-container">
        <h2>Error Loading Profile</h2>
        <p>{error}</p>
      </div>
    );
  }

  return (
    <div className="self-container">
      {/* Login/Signup Prompt Overlay */}
      {!isLoggedIn && (
        <div className="overlay">
          <div className="login-prompt">
            <h2>Please Log In or Sign Up</h2>
            <p>Access your profile, reviews, and favorites.</p>
            <div className="button-group">
              <Link to="/login" className="button login-button">Login</Link>
              <Link to="/signup" className="button signup-button">Sign Up</Link>
            </div>
          </div>
        </div>
      )}

      {/* Consolidated Loading State Overlay */}
      {isLoggedIn && (isUserLoading || isPostsLoading) && (
        <div className="loading-overlay">
          <div className="loading-spinner"></div>
          <p>Loading Profile...</p>
        </div>
      )}

      {/* Main Content: Render only when logged in AND not loading */}
      {isLoggedIn && !isUserLoading && !isPostsLoading && (
        <>
          {/* User profile section */}
          <section className="user-profile">
            <img
              className="avatar"
              alt={`${username || 'User'}'s Avatar`}
              src={picture || defaultAvatar}
              onError={(e) => { e.target.onerror = null; e.target.src=defaultAvatar; }}
            />
            <div className="user-info">
              <h1 className="user-name">{username}</h1>
              <button className="pref-box" onClick={() => setModalOpen(true)}>
              {username}'s Food Choices
         </button>
         {modalOpen && <Modal onClose={() => setModalOpen(false)} />}
              <div className="user-desc-container">
                <div className="desc-wrapper">
                  {!isEditing ? (
                    <p className="user-desc">
                      {description || "Edit profile to add a description."}
                    </p>
                  ) : (
                    <div className="edit-container">
                      <textarea
                        className="user-desc-edit"
                        value={newDescription}
                        onChange={(e) => {
                          const val = e.target.value;
                          if (val.length <= 200) {
                            setNewDescription(val);
                          }
                        }}
                        onBlur={handleDescriptionSave}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            handleDescriptionSave();
                          }
                        }}
                        autoFocus
                        maxLength={200}
                        rows={3}
                        aria-label="Edit profile description"
                      />
                      <p className={`char-count ${newDescription.length >= 200 ? 'limit-reached' : ''}`}>
                        {newDescription.length}/200
                      </p>
                    </div>
                  )}
                </div>
                {!isEditing && (
                  <img
                    src={pencil}
                    alt="Edit description"
                    onClick={() => {
                      setNewDescription(description);
                      setIsEditing(true);
                    }}
                    className="edit-icon"
                    title="Edit description"
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setIsEditing(true); }}
                  />
                )}
              </div>
            </div>
            <div className="user-stats">
              <Link to="/followers" className="stat-link">
                <span className="stat-count">{followerCount}</span> Followers
              </Link>
              <Link to="/following" className="stat-link">
                <span className="stat-count">{followingCount}</span> Following
              </Link>
            </div>
          </section>

         



          {/* Tab bar */}
          <nav className="reviews-favorites-bar">
            <div
              className={`tab ${selectedTab === "reviews" ? "tab-active" : ""}`}
              onClick={() => setSelectedTab("reviews")}
            >
              Reviews ({posts?.length || 0})
            </div>
            <div
              className={`tab ${selectedTab === "favorites" ? "tab-active" : ""}`}
              onClick={() => setSelectedTab("favorites")}
            >
              Favorites ({favorites?.length || 0})
            </div>
          </nav>

          {/* Content */}
          <div className="tab-content">
            {selectedTab === "reviews" ? <DishList /> : <FavoritesList />}
          </div>
        </>
      )}
    </div>
  );
};

export default Self;